import type { Component } from '../router';
import { icon, type IconName } from '../components/icons';
import { ApiError } from '../services/api';

/**
 * ErrorScreen — the shared, viewport-adaptive 40x / failure surface for the
 * SPA (, the client side of the RBAC policy). It mirrors
 * ComingSoonPage's centered layout and the server deny page's tone, so an
 * in-app refusal looks like the rest of the portal on phone and desktop
 * rather than a raw error.
 *
 * Kinds:
 *  - forbidden (403): the caller's role can't reach this (the RBAC denial).
 *  - notFound  (404): the page/record doesn't exist — also the router's
 *    unknown-route fallback.
 *  - offline:        the browser can't reach the portal.
 *  - generic:        any other unexpected failure.
 *
 * Copy never names the rule that denied the request and never carries PII.
 */

export type ErrorKind = 'forbidden' | 'notFound' | 'offline' | 'generic';

type ErrorAction = { label: string; href: string } | { label: string; onClick: () => void };

export interface ErrorScreenOptions {
  kind?: ErrorKind;
  /** Override the preset title. */
  title?: string;
  /** Override the preset copy. */
  copy?: string;
  /** Override the preset action; pass null to render no action. */
  action?: ErrorAction | null;
}

interface Preset {
  icon: IconName;
  title: string;
  copy: string;
  tone: 'neutral' | 'warn';
  action: ErrorAction;
}

const reload = (): void => window.location.reload();

const PRESETS: Record<ErrorKind, Preset> = {
  forbidden: {
    icon: 'lock',
    title: "You don't have access to this",
    copy: 'This part of the portal is limited to other roles. If you think you should have access, ask your administrator.',
    tone: 'neutral',
    action: { label: 'Back to home', href: '/' },
  },
  notFound: {
    icon: 'compass',
    title: "We can't find that page",
    copy: "The page you're looking for doesn't exist, or it may have moved.",
    tone: 'neutral',
    action: { label: 'Back to home', href: '/' },
  },
  offline: {
    icon: 'alert',
    title: "You're offline",
    copy: "We can't reach the portal right now. Check your connection and try again.",
    tone: 'warn',
    action: { label: 'Try again', onClick: reload },
  },
  generic: {
    icon: 'alert',
    title: 'Something went wrong',
    copy: 'We hit an unexpected problem. Please try again in a moment.',
    tone: 'warn',
    action: { label: 'Try again', onClick: reload },
  },
};

export class ErrorScreen implements Component {
  private root: HTMLElement | null = null;

  constructor(private opts: ErrorScreenOptions = {}) {}

  mount(host: HTMLElement): void {
    const preset = PRESETS[this.opts.kind ?? 'generic'];

    const section = document.createElement('section');
    section.className = 'error-screen';
    section.setAttribute('role', 'alert');

    const iconBox = document.createElement('div');
    iconBox.className = 'error-screen__icon';
    if (preset.tone === 'warn') iconBox.classList.add('error-screen__icon--warn');
    iconBox.appendChild(icon(preset.icon));

    const title = document.createElement('h1');
    title.className = 'error-screen__title';
    title.textContent = this.opts.title ?? preset.title;

    const copy = document.createElement('p');
    copy.className = 'error-screen__copy';
    copy.textContent = this.opts.copy ?? preset.copy;

    section.append(iconBox, title, copy);

    const action = this.opts.action === undefined ? preset.action : this.opts.action;
    if (action) section.appendChild(actionElement(action));

    host.append(section);
    this.root = section;
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }
}

function actionElement(action: ErrorAction): HTMLElement {
  if ('href' in action) {
    const a = document.createElement('a');
    a.className = 'error-screen__action';
    a.href = action.href;
    a.textContent = action.label;
    return a;
  }
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'error-screen__action';
  btn.textContent = action.label;
  btn.addEventListener('click', action.onClick);
  return btn;
}

/**
 * errorKind maps a thrown value to the right ErrorScreen kind. An ApiError's
 * status drives it (403 → forbidden, 404 → notFound, anything else → generic);
 * a non-ApiError throw while the browser reports itself offline → offline,
 * otherwise generic.
 */
export function errorKind(err: unknown): ErrorKind {
  if (err instanceof ApiError) {
    if (err.status === 403) return 'forbidden';
    if (err.status === 404) return 'notFound';
    return 'generic';
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  return 'generic';
}

/**
 * errorScreenFor builds an ErrorScreen for a caught error — the one call a page
 * makes when a page-level fetch fails so the whole screen falls back to the
 * shared surface instead of a broken state. overrides win over the preset.
 */
export function errorScreenFor(err: unknown, overrides: ErrorScreenOptions = {}): ErrorScreen {
  return new ErrorScreen({ kind: errorKind(err), ...overrides });
}
